import { formatDistanceToNowStrict } from 'date-fns';
import {
    getCouponDiscount,
    getEffectiveCouponEndDate,
    applyCouponOverwrites,
} from '../../utils/coupon-utils';

/**
 * @typedef {object} StripeDiscount
 * @property {import('stripe').Stripe.Coupon} coupon
 */

/**
 *
 * @param {{discount: StripeDiscount, isTrial: boolean}} props
 * @returns {JSX.Element|null}
 */
export default function CouponBanner({ discount, isTrial = false }) {
    // Early return if no discount or coupon
    if (!discount || !discount.coupon || !discount.coupon.id) {
        return null;
    }

    // Don't show banner if coupon is explicitly invalid
    if (discount.coupon.valid === false) {
        return null;
    }

    // Apply coupon overwrites if needed
    const discountForCalculation = applyCouponOverwrites(discount);
    const coupon = discountForCalculation?.coupon;

    let durationText;
    let durationInMonthsText = 'month';

    // Calculate the effective end date for the coupon
    const effectiveEndDate = getEffectiveCouponEndDate(discountForCalculation);

    if (effectiveEndDate) {
        durationInMonthsText = formatDistanceToNowStrict(effectiveEndDate, { addSuffix: false });
    }
    // Do a primitive validation if the coupon ID is conform
    // with our naming convention. See https://www.notion.so/ghost/How-to-create-marketing-offers-420239887ba44b229476a94588c0f48c?pvs=4
    const hasReadableID = coupon.id.match(/^[A-Z\d]+$/);
    const discountText = getCouponDiscount({ coupon, calculateTotal: false, format: true });

    switch (coupon.duration) {
        case 'once':
            durationText = isTrial ? 'your first payment' : 'your next payment';
            break;
        case 'forever':
            durationText = 'forever';
            break;
        case 'repeating':
            durationText = `for the next ${durationInMonthsText}`;
            break;
        default:
            break;
    }

    return (
        <>
            {hasReadableID ? (
                <div className="coupon">
                    Coupon: {coupon.id.toUpperCase()} has been applied to your account. You'll get{' '}
                    {discountText} off {durationText}.
                </div>
            ) : (
                <div className="coupon">
                    A coupon has been applied to your account. You'll get {discountText} off{' '}
                    {durationText}.
                </div>
            )}
        </>
    );
}
